import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Star, 
  Heart, 
  MessageCircle, 
  Download,
  Send,
  Reply,
  MoreVertical,
  Trash2,
  ArrowRight,
  PlusSquare,
  Copy,
  Check
} from 'lucide-react'
import { Image, Comment } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'
import MoveImageDialog from './MoveImageDialog'
import CreateSceneDialog from './CreateSceneDialog'

interface ImageLightboxProps {
  open: boolean
  onClose: () => void
  image: Image | undefined
  images: Image[]
  onImageSelect: (imageId: string) => void
  initialShowComments?: boolean
  projectTitle?: string
  sceneTitle?: string
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  onClose,
  image,
  images,
  onImageSelect,
  initialShowComments = false,
  projectTitle = 'project',
  sceneTitle = 'scene'
}) => {
  const [showComments, setShowComments] = useState(initialShowComments)
  const [newComment, setNewComment] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [createSceneDialogOpen, setCreateSceneDialogOpen] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)
  
  const { user } = useAuthStore()

  const handleCopyPrompt = async () => {
    if (!image?.prompt) return
    try {
      await navigator.clipboard.writeText(image.prompt)
      setPromptCopied(true)
      setTimeout(() => setPromptCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy prompt:', error)
    }
  }

  // Set initial comment state when opening
  useEffect(() => {
    if (open) {
      setShowComments(initialShowComments)
    }
  }, [open, initialShowComments])
  
  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, onClose])
  const { setMainImage, toggleFavorite, addComment, addReply, deleteComment, deleteImage, loading } = useProjectStore()

  const currentIndex = images.findIndex(img => img?.id === image?.id)
  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < images.length - 1

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return
      
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          if (canGoPrev) handlePrevious()
          break
        case 'ArrowRight':
          if (canGoNext) handleNext()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, canGoPrev, canGoNext])

  const handlePrevious = () => {
    if (canGoPrev) {
      onImageSelect(images[currentIndex - 1].id)
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      onImageSelect(images[currentIndex + 1].id)
    }
  }

  const handleSetMainImage = async () => {
    if (!image) return
    try {
      await setMainImage(image.id, image.sceneId)
    } catch (error) {
      console.error('Failed to set main image:', error)
    }
  }

  const handleToggleFavorite = async () => {
    if (!image) return
    try {
      await toggleFavorite(image.id)
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDownload = async () => {
    if (!image) return
    try {
      // Fetch the image with proper CORS settings
      const response = await fetch(image.url, {
        mode: 'cors'
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch image')
      }
      
      const blob = await response.blob()
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Create filename: projectname_scenename_image#
      const imageIndex = images.findIndex(img => img.id === image.id) + 1
      
      // Get file extension from blob type or URL
      let extension = 'jpg'
      if (blob.type) {
        extension = blob.type.split('/')[1] || 'jpg'
      } else if (image.url.includes('.')) {
        const urlParts = image.url.split('.')
        const lastPart = urlParts[urlParts.length - 1].split('?')[0]
        if (lastPart.match(/^(jpg|jpeg|png|gif|webp)$/i)) {
          extension = lastPart.toLowerCase()
        }
      } else if (image.metadata?.format) {
        extension = image.metadata.format.split('/')[1] || 'jpg'
      }
      
      // Sanitize project and scene names for filename
      const sanitizeFilename = (str: string) => str.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const sanitizedProject = sanitizeFilename(projectTitle)
      const sanitizedScene = sanitizeFilename(sceneTitle)
      
      const filename = `${sanitizedProject}_${sanitizedScene}_${imageIndex}.${extension}`
      link.download = filename
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Cleanup
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download image:', error)
      alert('Failed to download image. Please try again.')
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!image || !newComment.trim() || !user) return

    try {
      await addComment(image.id, newComment.trim(), user.uid, user.email || '')
      setNewComment('')
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  const handleAddReply = async (e: React.FormEvent, commentId: string) => {
    e.preventDefault()
    if (!replyText.trim() || !user) return

    try {
      await addReply(commentId, replyText.trim(), user.uid, user.email || '')
      setReplyText('')
      setReplyTo(null)
    } catch (error) {
      console.error('Failed to add reply:', error)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await deleteComment(commentId, image!.id)
      } catch (error) {
        console.error('Failed to delete comment:', error)
      }
    }
  }

  const getTimeAgo = (date: string) => {
    const now = new Date()
    const past = new Date(date)
    const diffInMinutes = Math.floor((now.getTime() - past.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 30) return `${diffInDays}d ago`
    
    return past.toLocaleDateString()
  }

  if (!open || !image) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        {/* Main Image Area - Scrollable for 100% resolution */}
        <div className="flex-1 flex flex-col relative overflow-auto">
          {/* Navigation Arrows - Fixed position */}
          {canGoPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrevious()
              }}
              className="fixed left-4 top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-20"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}

          {canGoNext && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNext()
              }}
              className="fixed top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-20"
              style={{ right: showComments ? '412px' : '16px' }}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Image Container - Shows 100% resolution, centered and scrollable */}
          <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
            <motion.img
              key={image.id}
              src={image.url}
              alt={image.name}
              className="max-w-none m-auto"
              style={{
                width: 'auto',
                height: 'auto'
              }}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

            {/* Top Controls */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
              <div className="flex items-center space-x-2 pointer-events-auto">
                {image.isMain && (
                  <span className="glass-button px-3 py-1 text-sm text-yellow-300 bg-yellow-500/20">
                    Main Image
                  </span>
                )}
                {image.isFavorite && (
                  <span className="glass-button px-3 py-1 text-sm text-red-300 bg-red-500/20">
                    Favorite
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-2 pointer-events-auto">
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMoreMenu(!showMoreMenu)
                    }}
                    className="glass-button p-2 hover:bg-white/20"
                  >
                    <MoreVertical className="w-5 h-5 text-white" />
                  </button>

                  <AnimatePresence>
                    {showMoreMenu && (
                      <>
                        <div 
                          className="fixed inset-0 z-40"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowMoreMenu(false)
                          }}
                        />
                        <motion.div
                          className="absolute right-0 top-full mt-2 w-48 glass-card border border-white/20 z-50 shadow-xl"
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="py-1">
                            <button
                              onClick={() => {
                                handleSetMainImage()
                                setShowMoreMenu(false)
                              }}
                              disabled={loading || image.isMain}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Star className="w-4 h-4" />
                              <span>{image.isMain ? 'Main Image' : 'Set as Main'}</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                handleToggleFavorite()
                                setShowMoreMenu(false)
                              }}
                              disabled={loading}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Heart className="w-4 h-4" />
                              <span>{image.isFavorite ? 'Remove Favorite' : 'Add Favorite'}</span>
                            </button>

                            <button
                              onClick={() => {
                                handleDownload()
                                setShowMoreMenu(false)
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download</span>
                            </button>

                            <hr className="border-white/10 my-1" />

                            <button
                              onClick={() => {
                                setMoveDialogOpen(true)
                                setShowMoreMenu(false)
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                            >
                              <ArrowRight className="w-4 h-4" />
                              <span>Move to scene</span>
                            </button>
                            <button
                              onClick={() => {
                                setCreateSceneDialogOpen(true)
                                setShowMoreMenu(false)
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                            >
                              <PlusSquare className="w-4 h-4" />
                              <span>Create a scene</span>
                            </button>

                            <hr className="border-white/10 my-1" />
                            
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this image?')) {
                                  deleteImage(image.id)
                                  setShowMoreMenu(false)
                                  onClose()
                                }
                              }}
                              disabled={loading}
                              className="w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/20 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onClose()
                  }}
                  className="glass-button p-2 hover:bg-white/20"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

          {/* Bottom Info */}
          <div className="flex-shrink-0 mt-4">
            <div className="glass-card p-4">
              {/* Prompt Display - Clickable to copy */}
              {image.prompt && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCopyPrompt()
                  }}
                  className="w-full mb-3 group"
                >
                  <div className={`relative bg-white/5 hover:bg-white/10 border rounded-xl p-3 transition-all text-left ${
                    promptCopied 
                      ? 'border-green-500/50 bg-green-500/10' 
                      : 'border-white/10 hover:border-purple-500/30'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-white/80 text-sm leading-relaxed flex-1">
                        {image.prompt}
                      </p>
                      <div className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                        promptCopied 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-white/10 text-white/50 group-hover:text-purple-400 group-hover:bg-purple-500/20'
                      }`}>
                        {promptCopied ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-white/40 mt-2">
                      {promptCopied ? 'Copied to clipboard!' : 'Click to copy prompt'}
                    </p>
                  </div>
                </button>
              )}
              
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1 mr-4">
                  <h3 className="text-white font-medium">{image.name}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSetMainImage()
                    }}
                    className={`glass-button p-2 ${
                      image.isMain ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                    }`}
                    disabled={loading}
                  >
                    <Star className="w-4 h-4" fill={image.isMain ? 'currentColor' : 'none'} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleFavorite()
                    }}
                    className={`glass-button p-2 ${
                      image.isFavorite ? 'text-red-400' : 'text-white hover:text-red-400'
                    }`}
                    disabled={loading}
                  >
                    <Heart className="w-4 h-4" fill={image.isFavorite ? 'currentColor' : 'none'} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowComments(!showComments)
                    }}
                    className="glass-button p-2 hover:bg-white/20 flex items-center space-x-1"
                  >
                    <MessageCircle className="w-4 h-4 text-white" />
                    <span className="text-white text-xs">
                      {image.comments.reduce((total, comment) => total + 1 + comment.replies.length, 0)}
                    </span>
                  </button>

                  <button 
                    onClick={handleDownload}
                    className="glass-button p-2 text-white hover:text-blue-400"
                    title="Download image"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-white/70">
                <span>Uploaded {getTimeAgo(image.uploadedAt)}</span>
                <span>{currentIndex + 1} of {images.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Sidebar */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              className="w-96 bg-black/80 backdrop-blur-sm border-l border-white/10 flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Comments Header */}
              <div className="p-4 border-b border-white/10">
                <h3 className="text-white font-medium">Comments</h3>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {image.comments.map((comment) => (
                  <div key={comment.id} className="space-y-3">
                    {/* Main Comment */}
                    <div className="glass-card p-3">
                      <div className="flex items-start justify-between mb-2">
                                                 <div className="flex items-center space-x-2">
                           <div className="w-6 h-6 bg-primary-500/30 rounded-full flex items-center justify-center">
                             <span className="text-xs text-white">
                               {comment.userEmail.charAt(0).toUpperCase()}
                             </span>
                           </div>
                           <span className="text-white text-sm font-medium">
                             {comment.userEmail}
                           </span>
                           <span className="text-white/50 text-xs">
                             {getTimeAgo(comment.createdAt)}
                           </span>
                         </div>
                         
                         {user?.uid === comment.userId && (
                           <button
                             onClick={() => handleDeleteComment(comment.id)}
                             className="glass-button p-1 hover:bg-red-500/20 text-red-300"
                           >
                             <Trash2 className="w-3 h-3" />
                           </button>
                         )}
                       </div>
                       
                       <p className="text-white/90 text-sm mb-2">{comment.content}</p>
                      
                      <button
                        onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                        className="text-primary-300 text-xs hover:text-primary-200"
                      >
                        Reply
                      </button>
                    </div>

                    {/* Replies */}
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="ml-6 glass-card p-3">
                                                 <div className="flex items-center space-x-2 mb-2">
                           <div className="w-5 h-5 bg-primary-500/30 rounded-full flex items-center justify-center">
                             <span className="text-xs text-white">
                               {reply.userEmail.charAt(0).toUpperCase()}
                             </span>
                           </div>
                           <span className="text-white text-sm font-medium">
                             {reply.userEmail}
                           </span>
                           <span className="text-white/50 text-xs">
                             {getTimeAgo(reply.createdAt)}
                           </span>
                         </div>
                         <p className="text-white/90 text-sm">{reply.content}</p>
                      </div>
                    ))}

                    {/* Reply Form */}
                    {replyTo === comment.id && (
                      <form 
                        onSubmit={(e) => handleAddReply(e, comment.id)}
                        className="ml-6 space-y-2"
                      >
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault()
                              if (replyText.trim()) {
                                handleAddReply(e as any, comment.id)
                              }
                            }
                          }}
                          placeholder="Write a reply... (Ctrl+Enter to submit)"
                          className="glass-input w-full h-20 resize-none text-sm"
                          maxLength={500}
                        />
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setReplyTo(null)
                              setReplyText('')
                            }}
                            className="glass-button text-white/70 hover:bg-white/10 px-3 py-1 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={!replyText.trim() || loading}
                            className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-3 py-1 text-sm disabled:opacity-50"
                          >
                            Reply
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                ))}

                {image.comments.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-white/30 mx-auto mb-4" />
                    <p className="text-white/70">No comments yet</p>
                    <p className="text-white/50 text-sm">Be the first to comment!</p>
                  </div>
                )}
              </div>

              {/* Add Comment Form */}
              <div className="p-4 border-t border-white/10">
                <form onSubmit={handleAddComment} className="space-y-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault()
                        if (newComment.trim()) {
                          handleAddComment(e as any)
                        }
                      }
                    }}
                    placeholder="Add a comment... (Ctrl+Enter to submit)"
                    className="glass-input w-full h-20 resize-none"
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50">
                      {newComment.length}/500
                    </span>
                    <button
                      type="submit"
                      disabled={!newComment.trim() || loading}
                      className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      <span>Comment</span>
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dialogs */}
        {moveDialogOpen && image && (
          <MoveImageDialog
            open={moveDialogOpen}
            onClose={() => setMoveDialogOpen(false)}
            imageId={image.id}
            currentSceneId={image.sceneId}
          />
        )}

        {createSceneDialogOpen && image && (
          <CreateSceneDialog
            open={createSceneDialogOpen}
            onClose={() => setCreateSceneDialogOpen(false)}
            projectId={image.projectId}
            initialImageId={image.id}
          />
        )}
      </motion.div>
    </AnimatePresence>
  )
}

export default ImageLightbox 